/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { UserProfile } from '../types';
import { Logger } from './logger';

/**
 * Shared Registration Service
 * Handles user registration, activation token generation, and account status management.
 */

const ACTIVATION_TOKEN_VALIDITY_HOURS = 24;

/**
 * Generate a random activation token
 */
export const generateActivationToken = (): string => {
    // Falls back to a pseudo-random generator if crypto is not available (rare in modern environments)
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
        return Array.from(crypto.getRandomValues(new Uint8Array(32)))
            .map(b => b.toString(16).padStart(2, '0'))
            .join('');
    }
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
};

/**
 * Generate activation token expiry date
 */
export const getActivationTokenExpiry = (): string => {
    const expiry = new Date();
    expiry.setHours(expiry.getHours() + ACTIVATION_TOKEN_VALIDITY_HOURS);
    return expiry.toISOString();
};

/**
 * Validate activation token
 */
export const isActivationTokenValid = (user: UserProfile): boolean => {
    if (!user.activationToken || !user.activationTokenExpiry) {
        return false;
    }

    const expiry = new Date(user.activationTokenExpiry);
    return expiry > new Date();
};

/**
 * Create user registration profile
 */
export const createRegistrationProfile = (email: string): Partial<UserProfile> => {
    const activationToken = generateActivationToken();
    const now = new Date().toISOString();
    const isDefaultAdmin = email.toLowerCase().includes('admin');
    Logger.info('Creating registration profile', { email });

    return {
        email,
        accountStatus: 'registered',
        profileEnabled: false,
        activationToken,
        activationTokenExpiry: getActivationTokenExpiry(),
        registrationDate: now,
        membershipLevel: isDefaultAdmin ? 'advanced' : 'basic',
        measurements: {},
        saveImageQuality: 'medium',
        connectedAccounts: {},
        generationCount: 0,
        generationLimitResetDate: now,
        favoritePoses: []
    };
};

/**
 * Confirm user email (first step of activation)
 */
export const confirmUserEmail = (user: UserProfile): UserProfile => {
    if (!isActivationTokenValid(user)) {
        throw new Error('Activation token is invalid or expired');
    }
    Logger.info('Confirming user email', { email: user.email });

    return {
        ...user,
        accountStatus: 'validated',
        activationToken: undefined,
        activationTokenExpiry: undefined
    };
};

/**
 * Enable user profile (admin step)
 */
export const enableUserProfile = (user: UserProfile): UserProfile => {
    Logger.info('Enabling user profile', { email: user.email });
    return {
        ...user,
        accountStatus: 'active',
        profileEnabled: true
    };
};

/**
 * Deactivate user account
 */
export const deactivateUserProfile = (user: UserProfile): UserProfile => {
    Logger.info('Deactivating user profile', { email: user.email });
    return {
        ...user,
        accountStatus: 'inactive',
        profileEnabled: false
    };
};

/**
 * Delete user profile (soft delete)
 */
export const deleteUserProfile = (user: UserProfile): UserProfile => {
    Logger.info('Deleting user profile', { email: user.email });
    return {
        ...user,
        accountStatus: 'deleted',
        profileEnabled: false
    };
};

/**
 * Check if user can login (must be activated AND enabled)
 */
export const canUserLogin = (user: UserProfile): boolean => {
    return user.accountStatus === 'active' && user.profileEnabled === true;
};

/**
 * Resend activation email (generates new token)
 */
export const regenerateActivationToken = (user: UserProfile): UserProfile => {
    if (user.accountStatus !== 'registered') {
        throw new Error('User account is not in registered state');
    }

    const newToken = generateActivationToken();
    Logger.info('Regenerating activation token', { email: user.email });
    return {
        ...user,
        activationToken: newToken,
        activationTokenExpiry: getActivationTokenExpiry()
    };
};
